import { Select } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAssetChooserState } from 'components/choosers/AssetChooser/useAssetChooserState';
import {
  AtlasGqlFilterOperator,
  AtlasGqlSortDirection,
  useGetSubComponentOptionsForChooserQuery,
} from 'types/atlas-graphql';

import { filterOption } from '../../helpers';
import { ClearIcon, SuffixIcon } from '../ComponentChooser.style';

export interface ISubComponentChooserProps {
  disabled: boolean;
  value?: string[]; // Currently selected items
  assetTypeNames?: string[]; // Array of asset type names to filter for
  assetParentIds?: string[]; // Array of asset UUIDs to query the children of
  onChange: any;
  mode?: 'multiple' | 'tags' | undefined;
}

export const SubComponentChooser: React.FunctionComponent<ISubComponentChooserProps> = ({
  disabled,
  value,
  assetTypeNames = [],
  assetParentIds = [],
  onChange,
  mode = 'multiple',
}) => {
  const { t } = useTranslation();
  const { data, loading } = useGetSubComponentOptionsForChooserQuery({
    skip: disabled,
    variables: {
      filterBy: [
        {
          key: 'assetTypeNames',
          values: assetTypeNames,
          operator: AtlasGqlFilterOperator.Equals,
        },
        {
          key: 'childrenOfIds',
          values: assetParentIds,
          operator: AtlasGqlFilterOperator.Equals,
        },
      ],
      sortBy: [
        {
          key: 'typeName',
          sort: AtlasGqlSortDirection.Asc,
        },
      ],
    },
  });

  const { handleChange, setVisible, chooserState } = useAssetChooserState({
    onChange,
    value,
  });
  // Keep search text across selections so users can bulk-select from a filtered list.
  // Clear when the dropdown closes (done selecting / dismissed the field).
  const [searchValue, setSearchValue] = useState('');

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
    setVisible(open);
  };

  return (
    <Select
      id="sub-component-chooser"
      placeholder={t('table.selectComponent', 'Select Component...')}
      loading={loading}
      allowClear
      showSearch
      filterOption={filterOption}
      mode={mode}
      maxTagCount={1}
      value={loading ? undefined : chooserState}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      disabled={disabled}
      clearIcon={<ClearIcon />}
      suffixIcon={chooserState ? null : <SuffixIcon />}
      autoClearSearchValue={false}
      searchValue={searchValue}
      onSearch={setSearchValue}
    >
      {data?.assetsWithMetadata?.items?.map(asset => (
        <Select.Option key={asset.id} className="sub-component-chooser-option" value={asset.id}>
          {asset.uniqueIdLabel}
        </Select.Option>
      ))}
    </Select>
  );
};
